import { supabase } from './supabase';

export type CoffeeStatus = 'waiting' | 'scheduled' | 'happened' | 'well_see';
export type PermissionLevel = 'anonymous' | 'first_names' | 'private';

export interface CoffeeSubmission {
  id: string;
  project_number: number;
  submitter_name: string;
  other_name: string;
  relationship: string;
  time_since: string | null;
  reason: string | null;
  talk_about: string | null;
  contact_info: string | null;
  wants_help: boolean;
  permission_level: PermissionLevel;
  status: CoffeeStatus;
  is_public: boolean;
  is_featured: boolean;
  featured_story: string | null;
  photo_url: string | null;
  admin_notes: string | null;
  date_submitted: string;
  date_scheduled: string | null;
  date_completed: string | null;
}

export interface CoffeeProjectStats {
  id: number;
  longest_time_apart: string;
  shortest_distance: string;
  updated_at: string;
}

export interface NewSubmission {
  submitter_name: string;
  other_name: string;
  relationship: string;
  time_since: string;
  reason: string | null;
  talk_about: string | null;
  contact_info: string | null;
  wants_help: boolean;
  permission_level: PermissionLevel;
}

const RELATIONSHIPS = [
  'Friend',
  'Parent',
  'Brother / Sister',
  'Grandparent',
  'Kid',
  'Old coworker',
  'Neighbor',
  'Former friend',
  'Teacher / Mentor',
  'Someone I owe an apology',
  'Someone who owes me one',
  'Someone I miss',
  'Other',
] as const;

const TIME_SINCE_OPTIONS = [
  'A few weeks',
  'A few months',
  'About a year',
  'A few years',
  'Way too long',
  "Honestly, I don't know",
] as const;

export { RELATIONSHIPS, TIME_SINCE_OPTIONS };

export async function fetchPublicSubmissions(): Promise<CoffeeSubmission[]> {
  const { data, error } = await supabase
    .from('coffee_submissions')
    .select('*')
    .eq('is_public', true)
    .order('project_number', { ascending: true });
  if (error) throw error;
  return (data ?? []) as CoffeeSubmission[];
}

export async function fetchSubmissionCount(): Promise<number> {
  const { count, error } = await supabase
    .from('coffee_submissions')
    .select('*', { count: 'exact', head: true });
  if (error) throw error;
  return count ?? 0;
}

export async function fetchProjectStats(): Promise<CoffeeProjectStats | null> {
  const { data, error } = await supabase
    .from('coffee_project_stats')
    .select('*')
    .eq('id', 1)
    .maybeSingle();
  if (error) throw error;
  return data as CoffeeProjectStats | null;
}

export async function fetchFeaturedSubmissions(): Promise<CoffeeSubmission[]> {
  const { data, error } = await supabase
    .from('coffee_submissions')
    .select('*')
    .eq('is_public', true)
    .eq('is_featured', true)
    .order('project_number', { ascending: false })
    .limit(6);
  if (error) throw error;
  return (data ?? []) as CoffeeSubmission[];
}

export async function createSubmission(input: NewSubmission): Promise<CoffeeSubmission> {
  // Fetch current max project_number to assign the next sequential number
  const { data: maxRow, error: maxError } = await supabase
    .from('coffee_submissions')
    .select('project_number')
    .order('project_number', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (maxError) throw maxError;

  const nextNumber = ((maxRow as { project_number: number } | null)?.project_number ?? 0) + 1;
  const isPublic = input.permission_level !== 'private';

  const insert = {
    project_number: nextNumber,
    submitter_name: input.submitter_name,
    other_name: input.other_name,
    relationship: input.relationship,
    time_since: input.time_since,
    reason: input.reason,
    talk_about: input.talk_about,
    contact_info: input.contact_info,
    wants_help: input.wants_help,
    permission_level: input.permission_level,
    status: 'waiting' as CoffeeStatus,
    is_public: isPublic,
  };

  const { data, error } = await supabase
    .from('coffee_submissions')
    .insert(insert)
    .select('*')
    .maybeSingle();

  if (error) throw error;
  return data as CoffeeSubmission;
}

export function statusLabel(status: CoffeeStatus): string {
  switch (status) {
    case 'waiting': return 'Waiting';
    case 'scheduled': return 'Scheduled';
    case 'happened': return 'Happened';
    case 'well_see': return "We'll See";
  }
}

export function statusColor(status: CoffeeStatus): string {
  switch (status) {
    case 'waiting': return 'text-amber-400 border-amber-400/40 bg-amber-400/10';
    case 'scheduled': return 'text-sky-400 border-sky-400/40 bg-sky-400/10';
    case 'happened': return 'text-emerald-400 border-emerald-400/40 bg-emerald-400/10';
    case 'well_see': return 'text-rose-400 border-rose-400/40 bg-rose-400/10';
  }
}

export function displayNames(s: CoffeeSubmission): { label: string; isAnon: boolean } {
  if (s.permission_level === 'anonymous' || !s.submitter_name) {
    return { label: 'Anonymous + Anonymous', isAnon: true };
  }
  return { label: `${s.submitter_name} + ${s.other_name}`, isAnon: false };
}
